/**
 * Root application component.
 * Sets up routing, theme, and layout structure.
 */

import { FC } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Box } from '@mui/material';
import { Header } from '@/components/Layout/Header';
import { Sidebar } from '@/components/Layout/Sidebar';
import { MainContent } from '@/components/Layout/MainContent';
import ProtectedRoute from '@/components/Auth/ProtectedRoute';
import Login from '@/pages/Login';
import ChangePassword from '@/pages/ChangePassword';
import { Dashboard } from '@/pages/Dashboard';
import { VlanDetailView } from '@/pages/VlanDetailView';
import { InterfaceDetailView } from '@/pages/InterfaceDetailView';
import { SwitchTopologyView } from '@/pages/SwitchTopologyView';
import { ConnectivityCheck } from '@/pages/ConnectivityCheck';
import { TransactionHistory } from '@/pages/TransactionHistory';
import { GraphTest } from '@/pages/GraphTest';
import { ComparisonView } from '@/pages/ComparisonView';
import { UserManagement } from '@/pages/UserManagement';
import { useUIStore } from '@/stores/uiStore';

// Create MUI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
});

const App: FC = () => {
  const { darkMode } = useUIStore();

  const appTheme = createTheme({
    ...theme,
    palette: {
      ...theme.palette,
      mode: darkMode ? 'dark' : 'light',
    },
  });

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <BrowserRouter>
        <Routes>
          {/* Public routes (no authentication required) */}
          <Route path="/login" element={<Login />} />
          <Route path="/change-password" element={<ChangePassword />} />

          {/* Protected routes (authentication required) */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Box sx={{ display: 'flex' }}>
                  <Header />
                  <Sidebar />
                  <MainContent>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/vlans" element={<VlanDetailView />} />
                      <Route path="/interfaces" element={<InterfaceDetailView />} />
                      <Route path="/switches" element={<SwitchTopologyView />} />
                      <Route path="/switch/:hostname" element={<SwitchTopologyView />} />
                      <Route
                        path="/switch/:hostname/compare"
                        element={<ComparisonView />}
                      />
                      <Route path="/connectivity" element={<ConnectivityCheck />} />
                      <Route path="/transactions" element={<TransactionHistory />} />
                      <Route path="/graph-test" element={<GraphTest />} />
                      <Route path="/settings" element={<Dashboard />} />
                      <Route path="/admin/users" element={<UserManagement />} />
                    </Routes>
                  </MainContent>
                </Box>
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
};

export default App;
